import { shallowEqual } from 'react-redux';
import { DateTools } from '@treezor/design-system';

import { useAppSelector } from '@/hooks/redux-helpers';

import { selectProfileKYCDocumentsByUserId, selectProfileKYCByUserId } from '@/store/selectors';
import { useProfileContext } from '@/features/context/ProfileContext';
import { IKycDocument, TKycDocumentStatus } from '@/features/kyc/store/models/kyc.model';

export function useKycDocumentList() {
  const { user } = useProfileContext();

  const allDocuments =
    useAppSelector(
      (state) =>
        selectProfileKYCDocumentsByUserId(state, user.userId)?.state?.map((kycDocument) => kycDocument.document),
      shallowEqual,
    ) || [];

  const isLoading = useAppSelector((state) => selectProfileKYCByUserId(state, user.userId)?.isLoading);

  const connectDocuments = allDocuments.filter((document) => document.isAgent);
  const baasDocuments = allDocuments.filter((document) => !document.isAgent);

  const connectDocumentOnly = connectDocuments.filter((document) => !document.metadata?.treezorDocumentId);
  const documentReviewed = baasDocuments.filter(
    (document) =>
      !!document.metadata?.connectDocumentId &&
      document?.treezorStatus === 'PENDING' &&
      document?.agentStatus === 'VALIDATED',
  );

  const mapDocumentByStatus = (documents: IKycDocument[], status: TKycDocumentStatus) =>
    documents.map((document) => ({ ...document, kycStatus: status }));

  // Connect
  const documentToReview = connectDocumentOnly.filter((document) => document.status === 'PENDING');
  const reviewedAgent = user.kycReview !== 1 && documentToReview?.length > 0 ? documentReviewed : [];
  const reviewedSubmit = user.kycReview !== 1 && documentToReview?.length === 0 ? documentReviewed : [];
  const refusedAgent = connectDocumentOnly.filter((connectDocument) => connectDocument.status === 'REFUSED');

  // Baas
  const pendingValidation = baasDocuments.filter((document) => {
    const hasLinkedConnectDocument = !!document.metadata?.connectDocumentId;
    return document.treezorStatus === 'PENDING' && (hasLinkedConnectDocument ? user.kycReview === 1 : true);
  });
  const validatedTreezor = baasDocuments.filter((baasDocument) => baasDocument.treezorStatus === 'VALIDATED');
  const refusedTreezor = baasDocuments.filter((baasDocument) => baasDocument.treezorStatus === 'REFUSED');
  const canceledDocuments = baasDocuments.filter((baasDocument) => baasDocument.treezorStatus === 'CANCELED');

  const documents = [
    ...mapDocumentByStatus(documentToReview, 'TO_REVIEW'),
    ...mapDocumentByStatus(reviewedAgent, 'REVIEWED'),
    ...mapDocumentByStatus(reviewedSubmit, 'REVIEWED_TO_SUBMIT'),
    ...mapDocumentByStatus(pendingValidation, 'PENDING_VALIDATION'),
    ...mapDocumentByStatus(validatedTreezor, 'VALIDATED'),
    ...mapDocumentByStatus(refusedAgent, 'REFUSED_CLIENT'),
    ...mapDocumentByStatus(refusedTreezor, 'REFUSED'),
    ...mapDocumentByStatus(canceledDocuments, 'CANCELED'),
  ].sort(
    (documentA, documentB) => DateTools.createDate(documentB?.createdAt) - DateTools.createDate(documentA?.createdAt),
  );

  return {
    isLoading,
    documents,
  };
}
